<template>
    <div v-if="loading" class="w-96 flex justify-center items-center h-96 bg-modal-bg relative">
        <Spinner :text="$t('common:text-loading')" />
    </div>
    <VariantUnavailable v-else-if="!variant?.id" class="p-6 lg:p-14 xl:p-16 bg-light rounded-panel" />
    <article v-else class="bg-modal-bg w-full max-w-6xl relative z-[51]">
        <ProductDetailPanel v-if="variant?.product?.type !== PRODUCT_TYPE.AUCTION" :variant="variant" />
        <ProductCategoryBreadcrumbs
            v-if="variant.product?.category"
            :categories="[ variant.product?.category ]"
            :base-route-code="baseRouteCode"
            :on-close="closeModal"
        />

        <Scrollbar class="w-full max-h-dmv mt-5" :options="{ scrollbars: { autoHide: 'never' } }">
            <div class="flex flex-col md:flex-row">
              <div class="md:w-1/2 pb-0 md:pb-5 px-5 md:px-3 py-5 md:pl-10">
                <ProductDetailGallery :variant="variant" />
                <ProductDetailInfoActions :variant="variant" position="outside" />
              </div>
              <div class="w-full md:w-1/2 px-5 md:px-3 py-0 md:py-5">
                <ProductDetailInfo :variant="variant" class="flex flex-col items-start overflow-hidden" size="modal" @select:tab:comments="selectTabComments" />
              </div>
            </div>

            <SupplierAdsPanel :except-id="variant.id" :supplier="variant?.product?.supplier" size="modal" />
            <RecommendPanel :except-id="variant.id" :category="variant?.product?.category" size="modal" />
            <CataloguePanel v-if="variant?.cataloguePosts?.length" :posts="variant.cataloguePosts" size="modal" />

            <div class="w-full flex items-center justify-center py-8">
              <Button size="big" :on-click="closeModal">
                <span class="text-sm px-9">{{ $t('text-close') }}</span>
              </Button>
            </div>
        </Scrollbar>
    </article>
</template>

<script setup>
import _ from 'lodash-es';
import { API_ENDPOINTS } from "@utils/api/endpoints";
import { ROUTES } from "@utils/routes";
import { PRODUCT_TYPE } from "@utils/constants";
import { useAuthStore } from '@stores/auth';
import { useSelectedStore } from '@stores/selected';
import Spinner from "@components/ui/loaders/spinner/spinner";
import Button from "@components/ui/button";
import VariantUnavailable from "@components/product/product-details/variant-unavailable";
import ProductDetailGallery from "@components/product/product-details/product-detail-gallery";
import ProductDetailPanel from "@components/product/product-details/product-detail-panel";
import ProductDetailInfo from "@components/product/product-details/product-detail-info";
import ProductCategoryBreadcrumbs from "@components/category/product-category-breadcrumbs";
import SupplierAdsPanel from "@components/product/product-panel/supplier-ads-panel";
import RecommendPanel from "@components/product/product-panel/recommend-panel";
import CataloguePanel from "@components/catalogue/catalogue-panel";
import Scrollbar from "@components/ui/scrollbar";
import ProductDetailInfoActions from "@components/product/product-details/product-detail-info-actions";

const loading = ref(true);
const { t } = useI18n();
const { $eventBus, $toast } = useNuxtApp();
const auth = useAuthStore();
const store = useSelectedStore();
const routeLang = useRouteLang();

const variant = ref({});
const setVariant = (data) => {
    _.assignIn(variant.value, data);
}

const baseRouteCode = computed(() => ({
    [PRODUCT_TYPE.OFFER]: ROUTES.CODE.OFFERS,
    [PRODUCT_TYPE.WANTED]: ROUTES.CODE.REQUESTS,
    [PRODUCT_TYPE.AUCTION]: ROUTES.CODE.AUCTIONS
})[variant.value?.product?.type] || ROUTES.CODE.OFFERS);

const reload = async () => {
    loading.value = true;
    const { data, error, status } = await useApiFetch(API_ENDPOINTS.ESHOP_PRODUCT_VARIANT_ID, {
      method: 'GET',
      path: {
        id: store.product.id
      },
      params: {
        locale: routeLang.locale.value
      }
    });
    if (status.value === 'error') {
        if (import.meta.client && !auth.reloadTimeout) {
          $toast.error(t('error.product-detail.load'));
        }
        sentryCaptureEvent({
          message: 'product-details-modal-view.reload',
          level: 'error',
          extra: { error: error.value }
        });
    } else {
      setVariant(data.value);
    }
    loading.value = false;
}

onServerPrefetch(async () => await reload());

const variantUpdate = (value) => {
  if (value.id === variant.value.id) {
    variant.value = { ...variant.value, ...value };
  }
};

onMounted(async () => {
  if (import.meta.server || navigator?.onLine) {
    await reload();
  } else {
    closeModal();
  }
  $eventBus.on('variant:update', variantUpdate);
  if (store.action === 'SELECT_TAB_COMMENTS') {
    $eventBus.emit('focus:input', 'comment');
  }
});

onUnmounted(() => {
  $eventBus.off('variant:update', variantUpdate);
});

const closeModal = () => {
    $eventBus.emit('modal:close');
}

const selectTabComments = (id) => {
    if (id === variant.value.id) {
        $eventBus.emit('focus:input', 'comment');
    }
}

</script>
